import { ENDPOINT_BASE } from "@api/constants";
import { enrichOrderWithProduct } from "../marketplaceOrder";
import useMyService from "@service/useMyService";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

const PRODUCT_QUERY_KEY = "product";
const DETAIL_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Loads product details for marketplace orders so dashboards show the
 * current listing name (e.g. remanufacturing service title) instead of a
 * stale or incorrect `productName` snapshot from the API.
 *
 * @param {Array<Record<string, unknown>>} orders
 * @returns {Array<Record<string, unknown>>}
 */
export function useEnrichOrdersWithProductNames(orders) {
  const queryClient = useQueryClient();
  const { getEntityById } = useMyService();

  const ordersWithProductId = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    const seen = new Set();
    return orders.filter((order) => {
      const id = order?.productId;
      if (id == null) return false;
      const key = String(id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [orders]);

  const detailQueries = useQueries({
    queries: ordersWithProductId.map((order) => ({
      queryKey: [PRODUCT_QUERY_KEY, order.productId],
      queryFn: ({ signal }) =>
        getEntityById({
          id: order.productId,
          endpoint: ENDPOINT_BASE.PRODUCT,
          signal,
        }),
      staleTime: DETAIL_STALE_TIME_MS,
      enabled: order.productId != null,
    })),
  });

  return useMemo(() => {
    if (!Array.isArray(orders)) return [];

    const productById = new Map();
    ordersWithProductId.forEach((order, index) => {
      const productId = order.productId;
      const cached = queryClient.getQueryData([PRODUCT_QUERY_KEY, productId]);
      const detail = detailQueries[index]?.data ?? cached;
      if (detail && typeof detail === "object") {
        productById.set(String(productId), detail);
      }
    });

    return orders.map((order) => {
      const product = productById.get(String(order?.productId ?? ""));
      return enrichOrderWithProduct(order, product);
    });
  }, [orders, ordersWithProductId, detailQueries, queryClient]);
}
